<script lang="ts" setup>
import type { KUICoreRegionSwitcherOption } from '~kui/components/KUI/Core/RegionSwitcher/KUICoreRegionSwitcher.vue';

import { provideUnifiedPublicLoader } from '~kui/composables/useUnifiedPublicLoaderContext';

import UnifiedNavigationIA from '~/components/Unified/IA/UnifiedNavigationIA.vue';
import UnifiedPublicBackButton from '~/components/Unified/IA/UnifiedPublicBackButton.vue';
import UnifiedPublicLoader from '~/components/Unified/Landing/UnifiedPublicLoader.vue';
import UnifiedLoggingOutLoader from '~/components/Unified/UnifiedLoggingOutLoader.vue';
import { provideIaBackFrame } from '~/composables/unified/useIaBackFrame';
import { useGeoRouting } from '~/composables/useGeoRouting';
import { useUnifiedLandingNav } from '~/composables/useUnifiedLandingNav';
import { useUnifiedPublicNavHidden, useUnifiedPublicNavLogoOnly } from '~/composables/useUnifiedPublicNav';
import { UNIFIED_ROUTE_INDEX } from '~/constants/routes/unified';

const route = useRoute();
const { t } = useI18n();
// Acquire before the first await — afterwards the Nuxt instance context is gone
// and useGeoRouting()'s useRuntimeConfig() throws.
const { regions, selectRegion } = useGeoRouting();

const options = computed(() => ({
  fullWidth: route.meta.unifiedPublic?.fullWidth ?? false,
  fixedHeader: route.meta.unifiedPublic?.fixedHeader ?? false,
  showLoader: route.meta.unifiedPublic?.showLoader ?? false,
}));

const loaderCtx = provideUnifiedPublicLoader();
if (!options.value.showLoader) {
  loaderCtx.loaderComplete.value = true;
  loaderCtx.loaderGone.value = true;
}

const navHidden = useUnifiedPublicNavHidden();
const navLogoOnly = useUnifiedPublicNavLogoOnly();
const accessibilityStore = useAccessibilityStore();
// Logging out clears the student before it leaves, so for that beat the page is the loader rather
// than the guest version of itself.
const authStore = useAuthStore();

const { navItems, iaNav, signInItem, upperFooterLinks, lowerFooterLinks, footerRegions } = await useUnifiedLandingNav();

const underBackButton = computed(() => iaNav.value && Boolean(route.meta.iaBack));
provideIaBackFrame(underBackButton);

const iaOwnWidth = computed(() => iaNav.value && Boolean(route.meta.ia?.fullWidth));
// Under the way back a page sits in the bar's column, as the button does: one that sets its own IA
// width keeps it, any other is put in that column here.
const inBackColumn = computed(() => underBackButton.value && !iaOwnWidth.value);
const fullWidth = computed(() => options.value.fullWidth || iaOwnWidth.value || underBackButton.value);

const handleFooterRegionSwitch = (region: KUICoreRegionSwitcherOption, event: MouseEvent): void => {
  // Clicking the current region is not a switch.
  if (region.isCurrent) {
    return;
  }

  // The footer routes through the preference-aware switch flow (which tracks
  // its own analytics) instead of navigating directly. The link's own
  // navigation is only cancelled once that flow has the click — otherwise the
  // footer stays a plain crawlable link that works without JS.
  const fullRegion = regions.value.find(candidate => candidate.url === region.url);
  if (fullRegion) {
    event.preventDefault();
    selectRegion(fullRegion, 'footer');
  }
};
</script>

<template>
  <div class="relative bg-background min-h-dvh overflow-x-clip">
    <UnifiedRegionGeoRouting />
    <UnifiedPublicLoader v-if="options.showLoader" />
    <p role="status" class="sr-only">
      {{ authStore.isloggingOut ? $t('general.common.loggingOut') : '' }}
    </p>
    <UnifiedLoggingOutLoader v-if="authStore.isloggingOut" />
    <KUIUnifiedLayoutPublic
      v-else
      :full-width="fullWidth"
      :logo-label="t('unified.home')"
      :nav-items="navItems"
      :home-link="UNIFIED_ROUTE_INDEX"
      :lower-footer-links="lowerFooterLinks"
      :upper-footer-links="upperFooterLinks"
      :footer-regions="footerRegions"
      :fixed-header="options.fixedHeader"
      :hide-nav="navHidden"
      :logo-only-nav="navLogoOnly"
      :animations-enabled="accessibilityStore.animationsEnabled"
      @region-switch="handleFooterRegionSwitch"
    >
      <template v-if="iaNav" #nav>
        <UnifiedNavigationIA :logo-only="navLogoOnly" :sign-in-item="signInItem" />
        <!-- Keyed so each page reads the history entry behind it, as the shell's per-page button does. -->
        <UnifiedPublicBackButton v-if="underBackButton" :key="route.path" />
      </template>
      <div :class="inBackColumn ? 'mx-auto w-full max-w-7xl px-4' : { 'md:px-16': !fullWidth }">
        <slot />
      </div>
    </KUIUnifiedLayoutPublic>
  </div>
</template>
